import { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import Modal from "../../../../../parts/modal-new";
import { getDateWithDayToDisplay } from "../../../../../utils/date-helpers";
import SvgIcon from "../../../../../parts/SvgIcon";
import { simpleHash } from "../../../../../utils/string-helpers";

export const DuplicateMealPlanModal = ({ locals }) => {
  const { t } = useTranslation("coaching-portal");
  const { query, mealPlan } = locals;
  const {
    date,
    athlete,
    mealPlan: mealPlanId,
    week = 1,
    dayType,
    optionId,
  } = query;
  const planMode = mealPlan?.planMode || "detailed";
  const isSimplified = planMode === "simplified";
  const { weeks = [], trainingDayOptions = [], restDayOptions = [] } = mealPlan;

  // Get meals based on planMode
  const meals =
    isSimplified && dayType
      ? // Simplified: get meals from the first option of trainingDayOptions or restDayOptions
        (dayType === "training"
          ? trainingDayOptions
          : restDayOptions)[0]?.meals?.map(({ type }) => type) || []
      : // Detailed: get meals from weeks structure
        weeks[0]?.days[0]?.meals?.map(({ type }) => type) || [];
  const dateToUse = athlete ? date : `Week ${week} ${date}`;
  const initialState = {
    selectedWeekIndex: 0,
    selectedDaysByWeek: {},
    selectedMeals: [],
  };
  const [state, setState] = useState(initialState);
  const { selectedWeekIndex, selectedDaysByWeek, selectedMeals } = state;
  const hasSelectedMeals = selectedMeals.length > 0;

  const hasSelectedDays = Object.values(selectedDaysByWeek).some(
    (days) => days.length > 0,
  );

  const canDuplicate = hasSelectedMeals && hasSelectedDays;

  /**
   * Function to select the meals
   * @param {*} mealType
   */
  const toggleMealSelection = (mealType) => {
    setState((prev) => {
      const isSelected = prev.selectedMeals.includes(mealType);
      return {
        ...prev,
        selectedMeals: isSelected
          ? prev.selectedMeals.filter((m) => m !== mealType)
          : [...prev.selectedMeals, mealType],
      };
    });
  };

  /**
   * Function to select the week
   * @param {*} index
   */
  const handleWeekSelect = (index) => {
    setState((prev) => ({
      ...prev,
      selectedWeekIndex: index,
    }));
  };

  /**
   * Function to select the days
   * @param {*} weekIndex
   * @param {*} dayIndex
   */
  const toggleDaySelection = (weekIndex, dayIndex) => {
    setState((prev) => {
      const weekDays = prev.selectedDaysByWeek[weekIndex] || [];
      const isSelected = weekDays.includes(dayIndex);

      return {
        ...prev,
        selectedDaysByWeek: {
          ...prev.selectedDaysByWeek,
          [weekIndex]: isSelected
            ? weekDays.filter((i) => i !== dayIndex)
            : [...weekDays, dayIndex],
        },
      };
    });
  };

  /**
   * Duplicate action
   */
  const handleDuplicate = () => {
    const payload = {
      initDate: dateToUse,
      isTemplate: !athlete,
      meals: selectedMeals,
      selectedDaysByWeek,
    };

    // Add simplified-specific data if needed
    if (isSimplified && dayType && optionId) {
      // For simplified, we need to send optionIds instead of indices
      const optionsArray =
        dayType === "training" ? trainingDayOptions : restDayOptions;

      // selectedDaysByWeek[0] contains selected indices from UI
      const selectedIndices = selectedDaysByWeek[0] || [];

      // Convert indices to optionIds
      const selectedOptionIds = selectedIndices
        .map((idx) => optionsArray[idx]?.optionId)
        .filter(Boolean);

      payload.planMode = "simplified";
      payload.dayType = dayType;
      payload.sourceOptionId = optionId;
      payload.selectedOptionIds = selectedOptionIds; // Array of target optionIds
    }

    const requestId = simpleHash(JSON.stringify(payload));

    const body = {
      ...payload,
      requestId,
    };
    submit(
      `/actions/meal-planner/duplicate-day-meal-plan?mealPlanId=${mealPlanId}&proxyAthlete=${athlete}`,
      {
        method: "POST",
        body,
      },
    );
  };

  /**
   * Reset the state
   */
  const handleClearAll = () => {
    setState(initialState);
  };

  return (
    <Modal
      className="duplicate-meal-plan-modal"
      title={t("editMealPlan.duplicateMealPlanModal.title", { date: getDateWithDayToDisplay(dateToUse) })}
      toClear={["date", "dayType", "optionId"]}
    >
      <div className="body">
        <div className="meals-title">
          <span>{t("editMealPlan.duplicateMealPlanModal.meals")}</span>
        </div>
        <div className="select-meals">
          <ul>
            {/* All button */}
            <li key="all-meals">
              <button
                type="button"
                onClick={() => {
                  const allSelected = meals.every((m) =>
                    selectedMeals.includes(m),
                  );
                  setState((prev) => ({
                    ...prev,
                    selectedMeals: allSelected ? [] : [...meals],
                  }));
                }}
              >
                <SvgIcon
                  src={`/img/new-icons/${
                    meals.length > 0 &&
                    meals.every((m) => selectedMeals.includes(m))
                      ? "checked-square.svg"
                      : "unchecked-square.svg"
                  }`}
                  noFilterOnLight={
                    meals.length > 0 &&
                    meals.every((m) => selectedMeals.includes(m))
                  }
                />
                <span>{t("editMealPlan.duplicateMealPlanModal.all")}</span>
              </button>
            </li>
            {/* All the meals */}
            {meals.map((item, index) => (
              <li key={`${item}-${index}`}>
                <button type="button" onClick={() => toggleMealSelection(item)}>
                  <SvgIcon
                    src={`/img/new-icons/${
                      selectedMeals.includes(item)
                        ? "checked-square.svg"
                        : "unchecked-square.svg"
                    }`}
                    noFilterOnLight={selectedMeals.includes(item)}
                  />
                  <span>{item}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className={`select-weeks ${isSimplified ? "simplified" : ""}`}>
          <span className="clipped font-bold">{t("editMealPlan.duplicateMealPlanModal.duplicateTo")}</span>
          <ul className="buttons">
            {weeks.map((week, index) => (
              <li key={week.rangeWeek?.start || index}>
                <button
                  onClick={() => handleWeekSelect(index)}
                  data-button-type={
                    selectedWeekIndex === index ? "primary" : "secondary"
                  }
                >
                  {t("editMealPlan.duplicateMealPlanModal.week", { n: index + 1 })}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="dates">
          <ul>
            {(() => {
              // Build days array based on planMode
              const days =
                isSimplified && dayType && optionId
                  ? // Simplified: get options from trainingDayOptions or restDayOptions, excluding current option
                    (dayType === "training"
                      ? trainingDayOptions
                      : restDayOptions
                    )
                      .map((option, idx) => ({
                        date: option.title || `Option ${idx + 1}`,
                        idx,
                        optionId: option.optionId,
                      }))
                      .filter(
                        ({ optionId: optId }) =>
                          optId?.toString() !== optionId?.toString(),
                      ) // Exclude current option
                  : // Detailed: use weeks structure
                    weeks[selectedWeekIndex]?.days?.reduce(
                      (acc, { date }, idx) => {
                        const shouldInclude = date
                          ? date !== dateToUse
                          : `Week ${selectedWeekIndex + 1} Day ${idx + 1}` !==
                            dateToUse;

                        if (shouldInclude) acc.push({ date, idx });
                        return acc;
                      },
                      [],
                    ) || [];

              // For simplified, always use index 0 since there are no weeks
              const weekIndexToUse = isSimplified ? 0 : selectedWeekIndex;
              const selectedDays = selectedDaysByWeek[weekIndexToUse] || [];
              const allSelected =
                days.length > 0 &&
                days.every(({ idx }) => selectedDays.includes(idx));

              return (
                <>
                  {/*  EveryDay */}
                  <li key="every-day">
                    <button
                      type="button"
                      onClick={() => {
                        setState((prev) => {
                          const weekIndexToUse = isSimplified
                            ? 0
                            : selectedWeekIndex;
                          return {
                            ...prev,
                            selectedDaysByWeek: {
                              ...prev.selectedDaysByWeek,
                              [weekIndexToUse]: allSelected
                                ? []
                                : days.map(({ idx }) => idx),
                            },
                          };
                        });
                      }}
                    >
                      <SvgIcon
                        src={`/img/new-icons/${
                          allSelected
                            ? "checked-square.svg"
                            : "unchecked-square.svg"
                        }`}
                        noFilterOnLight={allSelected}
                      />
                      <span className={!allSelected ? "opacity-70" : ""}>
                        {isSimplified ? t("editMealPlan.duplicateMealPlanModal.everyOption") : t("editMealPlan.duplicateMealPlanModal.everyday")}
                      </span>
                    </button>
                  </li>

                  {/* Days */}
                  {days.map(({ date, idx }) => {
                    // For simplified, use option title directly; for detailed, use getDateWithDayToDisplay
                    const label = isSimplified
                      ? date // date is already the option title
                      : date
                        ? getDateWithDayToDisplay(date)
                        : `Day ${idx + 1}`;

                    const isSelected = selectedDays.includes(idx);

                    return (
                      <li key={`${date}-${idx}`}>
                        <button
                          type="button"
                          onClick={() => {
                            // For simplified, use index 0 since there are no weeks
                            const weekIndexToUse = isSimplified
                              ? 0
                              : selectedWeekIndex;
                            toggleDaySelection(weekIndexToUse, idx);
                          }}
                        >
                          <SvgIcon
                            src={`/img/new-icons/${
                              isSelected
                                ? "checked-square.svg"
                                : "unchecked-square.svg"
                            }`}
                            noFilterOnLight={isSelected}
                          />
                          <span className={!isSelected ? "opacity-70" : ""}>
                            {label}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </>
              );
            })()}
          </ul>
        </div>

        <div className="footer">
          <button
            data-button-type="secondary"
            data-action="cancel"
            onClick={handleClearAll}
          >
            {t("editMealPlan.duplicateMealPlanModal.clearAll")}
          </button>
          <button
            type="submit"
            data-button-type="primary"
            disabled={!canDuplicate}
            onClick={handleDuplicate}
          >
            {t("editMealPlan.duplicateMealPlanModal.duplicate")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

DuplicateMealPlanModal.propTypes = {
  locals: PropTypes.object,
};
